<script>
import { useAlert } from 'dashboard/composables';
import { mapActions, mapGetters } from 'vuex';
import BaseModal from 'dashboard/components/dahreply/BaseModal.vue';
import {
  apiErrorDetail,
  apiErrorMessage,
} from '../../../../api/coLiving/errors';
import { FIELD_CLASS, SELECT_CLASS } from '../constants';
import { WEEKDAYS } from '../helpers';
import {
  emptyMember,
  emptyCoverageRow,
  emptyLeave,
  memberToForm,
  validateMember,
  splitRowError,
  memberPayload,
} from '../teamForm';
import FormField from './FormField.vue';

export default {
  name: 'TeamModal',
  components: { BaseModal, FormField },
  props: {
    member: { type: Object, default: null },
  },
  emits: ['close', 'saved'],
  data() {
    return {
      fieldClass: FIELD_CLASS,
      selectClass: SELECT_CLASS,
      weekdays: WEEKDAYS,
      form: this.member ? memberToForm(this.member) : emptyMember(),
      errors: {},
      preview: null,
      isSubmitting: false,
    };
  },
  computed: {
    ...mapGetters('coLivingCatalog', { activeRoles: 'getActiveRoles' }),
    ...mapGetters('coLivingCondos', { condos: 'getActiveCondos' }),
    isEdit() {
      return !!this.member;
    },
    // Deactivating re-routes the member's open tickets; show how many first.
    isDeactivating() {
      return this.isEdit && this.member.active && !this.form.active;
    },
    coverageError() {
      return splitRowError(this.errors.coverage);
    },
    leaveError() {
      return splitRowError(this.errors.leave);
    },
    // An archived role the member already holds must stay selectable.
    roleOptions() {
      const options = [...this.activeRoles];
      if (
        this.member &&
        !options.some(role => role.key === this.member.role_key)
      ) {
        options.push({
          key: this.member.role_key,
          label: this.member.role_label || this.member.role_key,
        });
      }
      return options;
    },
  },
  watch: {
    async isDeactivating(value) {
      this.preview = null;
      if (!value) return;
      try {
        this.preview = await this.fetchReroutePreview(this.member.id);
      } catch (err) {
        this.preview = null;
      }
    },
  },
  methods: {
    ...mapActions('coLivingTeam', [
      'addMember',
      'editMember',
      'fetchReroutePreview',
    ]),
    errorText(field) {
      return this.errors[field]
        ? this.$t(`CO_LIVING.TEAM.ERRORS.${this.errors[field]}`)
        : '';
    },
    rowErrorText(error, index, group) {
      return error && error.index === index
        ? this.$t(`CO_LIVING.TEAM.${group}_ERRORS.${error.code}`)
        : '';
    },
    addCoverage() {
      this.form.coverage.push(emptyCoverageRow());
    },
    removeCoverage(index) {
      this.form.coverage.splice(index, 1);
    },
    addLeave() {
      this.form.leave.push(emptyLeave());
    },
    removeLeave(index) {
      this.form.leave.splice(index, 1);
    },
    // Server-side refusals that name a field are shown on that field.
    showServerError(err) {
      const detail = apiErrorDetail(err);
      const messages = {
        phone_taken: ['phone', 'PHONE_TAKEN'],
        address_not_found: ['address', 'ADDRESS_NOT_FOUND'],
        invalid_hours: ['hours', 'HOURS_ORDER'],
        invalid_phone: ['phone', 'PHONE'],
        role_unknown: ['role_key', 'ROLE'],
      };
      const hit = messages[detail.code];
      if (hit) {
        this.errors = { [hit[0]]: hit[1] };
        return;
      }
      useAlert(apiErrorMessage(this, err, 'CO_LIVING.TEAM.SAVE_FAILED'));
    },
    async onSubmit() {
      this.errors = validateMember(this.form);
      if (Object.keys(this.errors).length) return;
      this.isSubmitting = true;
      const body = memberPayload(this.form);
      try {
        const result = this.isEdit
          ? await this.editMember({ id: this.member.id, fields: body })
          : await this.addMember(body);
        this.$emit('saved', result);
        this.$emit('close');
      } catch (err) {
        this.showServerError(err);
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<template>
  <BaseModal size="large" @close="$emit('close')">
    <template #title>
      {{
        isEdit
          ? $t('CO_LIVING.TEAM.EDIT_MEMBER')
          : $t('CO_LIVING.TEAM.ADD_MEMBER')
      }}
    </template>

    <form
      class="grid grid-cols-1 md:grid-cols-2 gap-4"
      data-testid="co-living-member-form"
      @submit.prevent="onSubmit"
    >
      <FormField
        :label="$t('CO_LIVING.TEAM.FIELDS.NAME')"
        for-id="co-living-member-name"
        :error="errorText('name')"
      >
        <input
          id="co-living-member-name"
          v-model="form.name"
          type="text"
          maxlength="120"
          :class="[fieldClass, errors.name ? 'border-red-500' : '']"
          data-testid="co-living-member-name"
        />
      </FormField>

      <FormField
        :label="$t('CO_LIVING.TEAM.FIELDS.ROLE')"
        for-id="co-living-member-role"
        :error="errorText('role_key')"
      >
        <select
          id="co-living-member-role"
          v-model="form.role_key"
          :class="[selectClass, errors.role_key ? 'border-red-500' : '']"
          data-testid="co-living-member-role"
        >
          <option value="" disabled>
            {{ $t('CO_LIVING.TEAM.FIELDS.ROLE_PLACEHOLDER') }}
          </option>
          <option v-for="role in roleOptions" :key="role.key" :value="role.key">
            {{ role.label }}
          </option>
        </select>
      </FormField>

      <FormField
        :label="$t('CO_LIVING.TEAM.FIELDS.PHONE')"
        for-id="co-living-member-phone"
        :error="errorText('phone')"
        :hint="$t('CO_LIVING.TEAM.FIELDS.PHONE_HINT')"
      >
        <input
          id="co-living-member-phone"
          v-model="form.phone"
          type="tel"
          maxlength="32"
          :class="[fieldClass, errors.phone ? 'border-red-500' : '']"
          data-testid="co-living-member-phone"
        />
      </FormField>

      <FormField
        :label="$t('CO_LIVING.TEAM.FIELDS.EMAIL')"
        for-id="co-living-member-email"
        :error="errorText('email')"
      >
        <input
          id="co-living-member-email"
          v-model="form.email"
          type="email"
          maxlength="255"
          :class="[fieldClass, errors.email ? 'border-red-500' : '']"
          data-testid="co-living-member-email"
        />
      </FormField>

      <div class="md:col-span-2">
        <FormField
          :label="$t('CO_LIVING.TEAM.FIELDS.ADDRESS')"
          for-id="co-living-member-address"
          :error="errorText('address')"
          :hint="$t('CO_LIVING.TEAM.FIELDS.ADDRESS_HINT')"
        >
          <input
            id="co-living-member-address"
            v-model="form.address"
            type="text"
            :class="[fieldClass, errors.address ? 'border-red-500' : '']"
            data-testid="co-living-member-address"
          />
        </FormField>
      </div>

      <div class="md:col-span-2">
        <FormField
          :label="$t('CO_LIVING.TEAM.FIELDS.HOURS')"
          :error="errorText('hours')"
          :hint="$t('CO_LIVING.TEAM.FIELDS.HOURS_HINT')"
        >
          <div class="flex items-center gap-3">
            <input
              v-model="form.hours_start"
              type="time"
              class="!w-36"
              :class="[fieldClass]"
              data-testid="co-living-member-hours-start"
            />
            <span class="text-sm text-slate-500">
              {{ $t('CO_LIVING.CALENDAR.TO') }}
            </span>
            <input
              v-model="form.hours_end"
              type="time"
              class="!w-36"
              :class="[fieldClass]"
              data-testid="co-living-member-hours-end"
            />
          </div>
        </FormField>
      </div>

      <section class="md:col-span-2" data-testid="co-living-coverage">
        <div class="flex items-center justify-between mb-1">
          <h3 class="text-sm font-medium text-slate-900 dark:text-slate-100">
            {{ $t('CO_LIVING.TEAM.COVERAGE.TITLE') }}
          </h3>
          <woot-button
            variant="clear"
            size="small"
            icon="add"
            type="button"
            data-testid="co-living-add-coverage"
            @click="addCoverage"
          >
            {{ $t('CO_LIVING.TEAM.COVERAGE.ADD') }}
          </woot-button>
        </div>
        <p class="mb-2 text-xs text-slate-500 dark:text-slate-400">
          {{ $t('CO_LIVING.TEAM.COVERAGE.HINT') }}
        </p>
        <div
          v-for="(row, index) in form.coverage"
          :key="`cov-${index}`"
          class="mb-2"
        >
          <div class="flex flex-wrap items-center gap-2">
            <select
              v-model.number="row.weekday"
              class="!w-32"
              :class="[selectClass]"
              :data-testid="`co-living-coverage-weekday-${index}`"
            >
              <option v-for="day in weekdays" :key="day" :value="day">
                {{ $t(`CO_LIVING.WEEKDAYS.${day}`) }}
              </option>
            </select>
            <select
              v-model="row.site_id"
              class="!w-44"
              :class="[selectClass]"
              :data-testid="`co-living-coverage-condo-${index}`"
            >
              <option value="">
                {{ $t('CO_LIVING.TEAM.COVERAGE.ANY_CONDO') }}
              </option>
              <option v-for="condo in condos" :key="condo.id" :value="condo.id">
                {{ condo.name }}
              </option>
            </select>
            <input
              v-model="row.start"
              type="time"
              class="!w-32"
              :class="[fieldClass]"
              :data-testid="`co-living-coverage-start-${index}`"
            />
            <span class="text-sm text-slate-500">
              {{ $t('CO_LIVING.CALENDAR.TO') }}
            </span>
            <input
              v-model="row.end"
              type="time"
              class="!w-32"
              :class="[fieldClass]"
              :data-testid="`co-living-coverage-end-${index}`"
            />
            <woot-button
              v-tooltip.top="$t('CO_LIVING.COMMON.DELETE')"
              variant="clear"
              size="small"
              color-scheme="alert"
              icon="delete"
              type="button"
              :data-testid="`co-living-coverage-remove-${index}`"
              :aria-label="$t('CO_LIVING.COMMON.DELETE')"
              @click="removeCoverage(index)"
            />
          </div>
          <p
            v-if="rowErrorText(coverageError, index, 'COVERAGE')"
            class="mt-1 text-xs text-red-500"
            :data-testid="`co-living-coverage-error-${index}`"
          >
            {{ rowErrorText(coverageError, index, 'COVERAGE') }}
          </p>
        </div>
        <p
          v-if="!form.coverage.length"
          class="text-xs text-slate-500 dark:text-slate-400"
        >
          {{ $t('CO_LIVING.TEAM.COVERAGE.OFFICE_BASED') }}
        </p>
      </section>

      <section class="md:col-span-2" data-testid="co-living-leave">
        <div class="flex items-center justify-between mb-1">
          <h3 class="text-sm font-medium text-slate-900 dark:text-slate-100">
            {{ $t('CO_LIVING.TEAM.LEAVE.TITLE') }}
          </h3>
          <woot-button
            variant="clear"
            size="small"
            icon="add"
            type="button"
            data-testid="co-living-add-leave"
            @click="addLeave"
          >
            {{ $t('CO_LIVING.TEAM.LEAVE.ADD') }}
          </woot-button>
        </div>
        <p class="mb-2 text-xs text-slate-500 dark:text-slate-400">
          {{ $t('CO_LIVING.TEAM.LEAVE.HINT') }}
        </p>
        <div
          v-for="(leave, index) in form.leave"
          :key="`leave-${index}`"
          class="mb-2"
        >
          <div class="flex flex-wrap items-center gap-2">
            <input
              v-model="leave.date_from"
              type="date"
              class="!w-40"
              :class="[fieldClass]"
              :data-testid="`co-living-leave-from-${index}`"
            />
            <span class="text-sm text-slate-500">
              {{ $t('CO_LIVING.CALENDAR.TO') }}
            </span>
            <input
              v-model="leave.date_to"
              type="date"
              class="!w-40"
              :class="[fieldClass]"
              :data-testid="`co-living-leave-to-${index}`"
            />
            <input
              v-model="leave.reason"
              type="text"
              maxlength="255"
              :placeholder="$t('CO_LIVING.TEAM.LEAVE.REASON')"
              class="flex-1 min-w-[10rem]"
              :class="[fieldClass]"
              :data-testid="`co-living-leave-reason-${index}`"
            />
            <woot-button
              v-tooltip.top="$t('CO_LIVING.COMMON.DELETE')"
              variant="clear"
              size="small"
              color-scheme="alert"
              icon="delete"
              type="button"
              :data-testid="`co-living-leave-remove-${index}`"
              :aria-label="$t('CO_LIVING.COMMON.DELETE')"
              @click="removeLeave(index)"
            />
          </div>
          <p
            v-if="rowErrorText(leaveError, index, 'LEAVE')"
            class="mt-1 text-xs text-red-500"
            :data-testid="`co-living-leave-error-${index}`"
          >
            {{ rowErrorText(leaveError, index, 'LEAVE') }}
          </p>
        </div>
      </section>

      <div class="md:col-span-2">
        <label
          class="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200"
        >
          <input
            v-model="form.active"
            type="checkbox"
            class="mb-0"
            data-testid="co-living-member-active"
          />
          {{ $t('CO_LIVING.COMMON.ACTIVE') }}
        </label>
        <p
          v-if="isDeactivating && preview"
          class="mt-1 text-xs text-amber-600 dark:text-amber-400"
          data-testid="co-living-reroute-preview"
        >
          {{ $t('CO_LIVING.TEAM.REROUTE_NOTE', { count: preview.count }) }}
        </p>
      </div>

      <div class="md:col-span-2 flex justify-end gap-3">
        <woot-button variant="clear" type="button" @click="$emit('close')">
          {{ $t('CO_LIVING.COMMON.CANCEL') }}
        </woot-button>
        <woot-button
          type="submit"
          :is-loading="isSubmitting"
          data-testid="co-living-member-save"
        >
          {{ $t('CO_LIVING.COMMON.SAVE') }}
        </woot-button>
      </div>
    </form>
  </BaseModal>
</template>
